import { auth, usersCollection, huntsCollection } from "@/firebaseConfig";
import { signInAnonymously, signInWithEmailAndPassword, User } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";

import { Hunt } from "@/models/hunt";

interface ErrorCode extends Error {
  code: string;
}

const UserService = {
  login: async (email: string, password: string) => {
    let errorMsg = "";
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      if (user != null) {
        const userDocRef = doc(usersCollection, user.uid);
        const userProfileSnap = await getDoc(userDocRef);
        const userCol = userProfileSnap.data();
        // if the userCol is empty, the collection has not yet been created, so create that first
        if (!userCol) {
          return createAdminUser(user)
        }
        return userCol
      }
      errorMsg = "User was returned null from database.";
    } catch (error) {
      switch ((error as ErrorCode).code) {
        case "auth/user-not-found": {
          errorMsg = "User is unknown. Please try again!";
          break;
        }
        case "auth/wrong-password": {
          errorMsg = "Wrong password entered. Please try again!";
          break;
        }
        case "auth/user-disabled": {
          errorMsg = "This user has been disabled.";
          break;
        }
        default: {
          errorMsg = "Error. Please contact website administrator";
          break;
        }
      }
    }
    return errorMsg;
  },
  newUserForHunt: async (
    hunt: Hunt & { usedCode?: string }
  ) => {
    try {
      const response = await signInAnonymously(auth);
      if (response.user) {
        const usedCode = hunt.usedCode ?? hunt.code[0]?.code ?? "";
        const user = {
          id: response.user.uid,
          huntId: hunt.id,
          huntName: hunt.name,
          huntCode: usedCode,
          createdAt: new Date(),
        };
        const userDocRef = doc(usersCollection, response.user.uid);
        const huntDocRef = doc(huntsCollection, hunt.id);
        const updatedCode = hunt.code.map((c) =>
          c.code.toLowerCase() === usedCode.toLowerCase()
            ? { ...c, codeCount: Math.max(0, c.codeCount - 1) }
            : c
        );
        await Promise.all([
          setDoc(userDocRef, user),
          updateDoc(huntDocRef, { code: updatedCode }),
        ]);
        return user;
      }
      throw new Error("no user credential created");
    } catch (error) {
      console.error("error creating new user for hunt: ", error);
      return null;
    }
  },
  signInAnon: async () => {
    if (!auth.currentUser) {
      await signInAnonymously(auth);
    }
  },
};

const createAdminUser = async (user: User) => {
  const adminUser = {
    id: user.uid,
    email: user.email,
    name: user.displayName || '',
    role: 'admin',
    createdAt: new Date(),
  };
  const userDocRef = doc(usersCollection, user.uid);
  await setDoc(userDocRef, adminUser);
  return adminUser;
}

export default UserService;
export { UserService };
